<template>
  <v-app>
    <Card class="bg-white shadow-md shadow-primary-50 py-5 px-10">
      <template #content>
        <!-- On desktop (Electron) this header row doubles as the window drag
             handle; the language button inside it is auto-excluded (no-drag). -->
        <div
          class="flex justify-between align-center"
          :class="{ 'titlebar-drag': isDesktop }"
        >
          <Image :src="loginLogo" alt="Image" width="150" />
          <Button class="flex gap-2 font-bold text-primary-900"
            @click="changeLanguage(currentLocal == 'en' ? 'ar' : 'en')" variant="text">
            <v-icon class="mt-0.5">{{ "mdi-earth" }}</v-icon>
            <p>{{ currentLocal == "en" ? "العربية" : "English" }}</p>
          </Button>
        </div>
      </template>
    </Card>

    <Card class="bg-white shadow-md shadow-primary-50 p-10 w-4/12 m-auto">
      <template #content>
        <Form v-slot="$form" :resolver="resolver" :initialValues="initialValues" @submit="loginSubmit"
          class="flex justify-center flex-col gap-5">
          <div v-if="isDesktop" class="flex flex-col gap-4">
            <label for="tenant">{{ $t("login.tenant") }}</label>
            <InputText name="tenant" type="text" :placeholder="$t('login.tenant_placeholder')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm" v-model="initialValues.tenant" />
            <Message v-if="$form.tenant?.invalid" severity="error" size="small" variant="simple">{{
              $form.tenant.error?.message }}</Message>
          </div>
          <div class="flex flex-col gap-4">
            <label for="username">{{ $t("login.username") }}</label>
            <InputText name="username" type="text" :placeholder="$t('login.auth_with_email_or_username')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm" v-model="initialValues.username" />
            <Message v-if="$form.username?.invalid" severity="error" size="small" variant="simple">{{
              $form.username.error?.message }}</Message>
          </div>
          <div class="flex flex-col gap-4">
            <label class="font-thin" for="username">{{
              $t("login.password")
              }}</label>
            <Password name="password" type="password" :placeholder="$t('login.password')"
              class="bg-surface-100 rounded-md py-3 px-4 font-thin text-sm" inputClass="w-full"
              v-model="initialValues.password" toggleMask :feedback="false" />
            <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{
              $form.password.error?.message }}</Message>
          </div>
          <Button
            class="bg-primary-500 hover:bg-primary-300 text-white py-5 rounded-xl shadow-md shadow-primary-100 mt-5 gap-2"
            type="submit" :label="$t('login.login')" :loading="loading" />

          <!-- Forgot Password Link -->
          <div class="text-center mt-3">
            <RouterLink :to="getbaseURL + '/forgot-password'"
              class="text-primary-900 hover:text-primary-700 text-sm underline">
              {{ $t("login.forgot_password") }}
            </RouterLink>
          </div>
        </Form>
      </template>
    </Card>

    <div class="relative">
      <div class="flex justify-center items-center font-semibold bg-gradient-to-r from-primary-500 to-primary-700 h-20">
        <h2 class="text-white text-sm text-center px-4">
          جميع الحقوق محفوظة © 2025
        </h2>
      </div>
    </div>
  </v-app>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import config from "../../public/config.json";
import { getTenant, isDesktop, setTenant } from "../services/tenant.js";
import Card from "primevue/card";
import Image from "primevue/image";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Form from "@primevue/forms/form";
import Message from "primevue/message";
import Password from "primevue/password";
import { z } from "zod";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import Swal from "sweetalert2";
import NotificationService from "@/services/NotificationService";

export default {
  components: {
    Card,
    Image,
    Button,
    InputText,
    Form,
    Message,
    Password,
  },
  data() {
    return {
      footerImage: require("../assets/artboard-bg-colored.png"),
      footergradient: require("../assets/gradient-bg.png"),
      loading: false,
      isDesktop,
      initialValues: {
        // Desktop: prefill with the last tenant the user entered (remembered).
        tenant: isDesktop ? getTenant() : "",
        username: "",
        password: "",
      },
      currentLang: this.$i18n.locale, // Set initial language
      resolver: zodResolver(
        z.object({
          // Tenant is entered on the login form for desktop builds only; on web
          // it is derived from the URL, so it is not part of the schema there.
          ...(isDesktop
            ? {
                tenant: z
                  .string()
                  .min(1, this.$t("login.tenant_rules.tenant_required")),
              }
            : {}),
          username: z
            .string()
            .min(1, this.$t("login.username_rules.username_required")),
          password: z
            .string()
            .min(1, this.$t("login.password_rules.password_required")),
        })
      ),
    };
  },

  methods: {
    ...mapMutations(["setLang"]),
    async authenticateUser() {
      this.loading = true;
      const emailSchema = z.string().email();
      // Ask for browser-notification permission while we still have a
      // user-gesture context. Used as a fallback for incoming-call rings
      // when audio autoplay is blocked.
      NotificationService.ensurePermission();
      // Desktop: the tenant comes from the form field; persist it so autoLogin
      // and post-login lookups can read it back. Web: derived from the URL.
      let domain;
      if (isDesktop) {
        domain = this.initialValues.tenant.trim();
        setTenant(domain);
      } else {
        domain = this.getDomain;
      }
      try {
        const resp = await this.$store.dispatch("login", {
          username: this.initialValues.username.trim(),
          isAtuhEmail: emailSchema.safeParse(this.initialValues.username.trim())
            .success,
          pass: this.initialValues.password,
          domain: domain,
          request: "login",
        });
        this.loading = false;
        if (!resp.success) {
          this.error = resp.message || this.$t("login.login_error");
          Swal.fire({
            icon: "error",
            text: resp.message || this.$t("login.login_error"),
          });
        } else {
          if (this.type === 5) {
            // Log out wallboard users instead of redirecting
            this.$store.dispatch("logout");
          } else if (this.domain === config.admin && this.type === 4) {
            this.$router.replace({ name: "Domains" });
          } else {
            this.$router.replace({ name: "Home" });
          }
        }
      } catch (err) {
        this.loading = false;
        this.error =
          err.response && err.response.status === 0
            ? "CORS Error: Please check your server configuration."
            : this.$t("login.network_error");
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: this.$t("login.network_error"),
        });
      }
    },

    loginSubmit({ valid }) {
      if (valid) {
        this.authenticateUser();
      }
    },

    resetValidation() {
      this.$refs.form.reset();
    },
    changeLanguage(locale) {
      const html = document.documentElement;
      html.dir = locale === "ar" ? "rtl" : "ltr";
      this.setLang(locale);
    },
  },

  computed: {
    getDomain() {
      return getTenant();
    },
    langClass() {
      return this.currentLang == "ar"
        ? "gradient-custom-ar"
        : "gradient-custom-en";
    },
    loginLogo() {
      const logosList = JSON.parse(this.logos);
      if (
        !logosList||
        !logosList?.login_logo ||
        logosList?.login_logo == ""
      ) {
        return require("../assets/go_ucaas_logo.png");
      } else {
        return logosList.login_logo;
      }
    },
    currentLocal() {
      return this.$vuetify.locale.current;
    },

    ...mapGetters([
      "isAuthenticated",
      "domain",
      "type",
      "logos",
      "getbaseURL",
    ]),
  },
};
</script>

<style scoped>
.lang-switcher {
  margin-right: 16px;
}

.v-btn-toggle .v-btn {
  border: 1px solid #006c54;
  background-color: #f5f5f5;
  color: #006c54;
  transition: all 0.3s ease;
}

.v-btn-toggle .v-btn:hover {
  background-color: #006c54;
  color: #ffffff;
}

.lang-selected {
  font-weight: bold;
  background-color: #006c54 !important;
  color: #ffffff !important;
}

.central {
  margin-top: 8%;
}
</style>
